import React, { useState, useEffect } from 'react';
import { Shield } from 'lucide-react';
import apiClient from '../../../../api/apiClient';
import { createPageUrl } from '../../../../utils';

interface PunishmentDetailTabProps {
  employeeId: string | null;
}

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
};

const formatPunishmentType = (type: string | null | undefined) => {
  if (!type) return '-';
  const typeMap: Record<string, string> = {
    verbal_warning: 'Peringatan Lisan',
    written_warning: 'Peringatan Tertulis',
    suspension: 'Skorsing',
    termination: 'Pemutusan Hubungan Kerja',
  };
  return typeMap[type] || type;
};

const formatViolationCategory = (category: string | null | undefined) => {
  if (!category) return '-';
  const categoryMap: Record<string, string> = {
    attendance: 'Kehadiran',
    performance: 'Kinerja',
    conduct: 'Perilaku',
    policy: 'Pelanggaran Kebijakan',
  };
  return categoryMap[category] || category;
};

export default function PunishmentDetailTab({ employeeId }: PunishmentDetailTabProps) {
  const [punishments, setPunishments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPunishments();
  }, []);

  const loadPunishments = async () => {
    setLoading(true);
    try {
      // Use user-specific endpoint that doesn't require HRIS permissions
      const response = await apiClient.get('/user/punishments');
      const punishments = response.punishments || [];
      setPunishments(punishments);
    } catch (error) {
      setPunishments([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold">Data Punishment</h3>
          <p className="text-gray-500 text-sm">Riwayat hukuman disiplin</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Data Punishment</h3>
        <p className="text-gray-500 text-sm">Riwayat hukuman disiplin</p>
      </div>

      {punishments.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Shield className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p>Tidak ada data punishment</p>
          <p className="text-sm mt-2">Pegawai ini belum pernah menerima hukuman disiplin</p>
        </div>
      ) : (
        <div className="space-y-4">
          {punishments.map((punishment: any, index: number) => (
            <div
              key={punishment.id || index}
              className="border-2 rounded-lg p-4 space-y-3 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex justify-between items-start">
                <h4 className="font-medium text-gray-900">
                  {formatPunishmentType(punishment.punishment_type)}
                </h4>
                <a
                  href={createPageUrl(`PunishmentForm?id=${punishment.id}`)}
                  className="text-sm text-emerald-600 hover:text-emerald-700 hover:underline"
                >
                  Lihat Detail
                </a>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kategori Pelanggaran
                  </label>
                  <div className="px-3 py-2 bg-white border border-gray-200 rounded-md text-gray-900">
                    {formatViolationCategory(punishment.violation_category)}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tanggal</label>
                  <div className="px-3 py-2 bg-white border border-gray-200 rounded-md text-gray-900">
                    {formatDate(punishment.punishment_date)}
                  </div>
                </div>
                {punishment.description && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Deskripsi
                    </label>
                    <div className="px-3 py-2 bg-white border border-gray-200 rounded-md text-gray-900 min-h-[60px]">
                      {punishment.description}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
